import React, { useState, useEffect } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";

export const ProcessingSteps = ({
  steps = [],
  currentStepIndex = 0,
  isProcessing = true,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (!collapsed && steps.length) {
      setAnimate(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimate(true);
        });
      });
    }
  }, [steps.length, collapsed]);

  useEffect(() => {
    if (!isProcessing && steps.length > 0) {
      const timer = setTimeout(() => {
        setCollapsed(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isProcessing, steps.length]);

  useEffect(() => {
    if (isProcessing) {
      setCollapsed(false);
    }
  }, [isProcessing]);

  if (!steps.length) return null;

  const toggleCollapsed = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCollapsed((prev) => !prev);
  };

  return (
    <div className="intermediate-steps-box">
      <div className="steps-header-row">
        <div
          className={`steps-header-title ${
            isProcessing ? "processing" : "done"
          }`}
        >
          {isProcessing ? (
            <span className="perplexity-thinking">
              Processing
              <span className="dots">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </span>
            </span>
          ) : (
            <span className="perplexity-done">✓ All Steps Completed</span>
          )}
        </div>

        <button
          className="steps-toggle-btn ms-2"
          onClick={toggleCollapsed}
          type="button"
          aria-label={collapsed ? "Expand steps" : "Collapse steps"}
        >
          {collapsed ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
        </button>
      </div>

      <div
        className={`perplexity-steps ${collapsed ? "hidden" : ""}`}
        style={{
          maxHeight: collapsed ? "0" : "1000px",
          opacity: collapsed ? 0 : 1,
          overflow: "hidden",
          transition: "max-height 0.3s ease, opacity 0.3s ease",
        }}
      >
        <div className="timeline-line" />

        <div className="step-row label-row animate-in">
          <div className="step-indicator">
            <div className="step-dot timeline-dot" />
          </div>
          <div className="step-content">
            <div className="step-title label">Intermediate steps</div>
          </div>
        </div>

        {steps.map((step, idx) => {
          const isActive = idx === currentStepIndex && isProcessing;
          const isDone = idx < currentStepIndex || !isProcessing;

          return (
            <div
              key={idx}
              className={`step-row ${
                isActive ? "active" : isDone ? "done" : "pending"
              } ${animate ? "animate-in" : ""}`}
              style={{ transitionDelay: `${idx * 140}ms` }}
            >
              <div className="step-indicator">
                {isDone ? (
                  <div className="step-checkmark">✓</div>
                ) : isActive ? (
                  <div className="step-pulse">
                    <div className="pulse-dot" />
                  </div>
                ) : (
                  <div className="step-number">{idx + 1}</div>
                )}
              </div>

              <div className="step-content">
                <div
                  className={`step-title ${
                    isActive ? "active" : isDone ? "done" : "pending"
                  }`}
                >
                  {step.title || step}
                </div>

                {step.description && (
                  <div className="step-description">{step.description}</div>
                )}

                {isActive && <div className="step-subtitle">Processing...</div>}
              </div>
            </div>
          );
        })}

        {!isProcessing && (
          <div className="step-row label-row finished animate-in">
            <div className="step-indicator">
              <div className="step-dot" />
            </div>
            <div className="step-content">
              <div className="step-title label">Finished</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
